import { useMemo } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { ChatbotConversation } from "app/containers/Chatbot/types";
import { useGetCheckInSummaryQuery } from "app/containers/TeamsTab/teamsCheckInApi";
import { useCoachBoConversations } from "./coachBoConversations";

// The check-in conversation the user can still act on, or null when there's
// no pending check-in. Prefers the pending thread and falls back to the most
// recent check-in conversation.
export const useActiveCheckInConversation = (
  userAccountId: number | null
): ChatbotConversation | null => {
  const { conversations } = useCoachBoConversations();

  // Same args as BoZoneActivityDetailSection so RTK Query shares the cached
  // request. hasPendingCheckIn is evaluated as of now regardless of the params.
  const { data } = useGetCheckInSummaryQuery(
    userAccountId !== null
      ? {
          userAccountId: userAccountId,
          mostRecentCompletedCheckIn: true,
        }
      : skipToken
  );

  const hasPendingCheckIn = !!data?.hasPendingCheckIn;
  const pendingThreadId = data?.pendingCheckInThreadId;

  return useMemo(() => {
    if (!hasPendingCheckIn) {
      return null;
    }

    const checkInConversations = conversations
      .filter((conversation) => !!conversation.weeklyCheckInLink)
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    return (
      checkInConversations.find(
        (conversation) => conversation.threadId === pendingThreadId
      ) ??
      checkInConversations[0] ??
      null
    );
  }, [conversations, hasPendingCheckIn, pendingThreadId]);
};
